import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import { toLowerCase } from 'utils/manipulateString';
import * as clientErrTypes from 'config/clientErrorTypes';

const errObjTypes = {
    CHUNK_LOAD_ERROR: 'chunkloaderror',
    TYPE_ERROR: 'typeerror',
    ABORT_ERROR: 'aborterror'
};

export function isOneOfErrors(checkers = [], errObj) {
    return checkers.some(c => c(errObj));
}
export function isChunkLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errCode = toLowerCase(errObj.code);
    const errMessage = toLowerCase(errObj.message);
    return [
        errName === errObjTypes.CHUNK_LOAD_ERROR,
        errCode === 'css_chunk_load_failed',
        errMessage.includes('loading chunk')
    ].some(e => e);
}
export function isWebKitError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && [errMessage.startsWith('webkit')].some(e => e);
}
export function isDNSLookupError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('無法找到指定主機名稱的伺服器')].some(e => e)
    );
}
export function isSSLConnectionError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        [errMessage.includes('發生ssl錯誤，無法建立與伺服器的安全連線')].some(e => e)
    );
}
export function isFetchNetworkError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.ABORT_ERROR ||
        (errName === errObjTypes.TYPE_ERROR &&
            [
                errMessage === '已取消',
                errMessage === 'cancelled',
                errMessage === '要求逾時。',
                errMessage === 'the request timed out.',
                errMessage === 'network request failed',
                errMessage === 'the network connection was lost.',
                errMessage === '網路連線中斷。',
                errMessage === 'internet 連線已斷開。',
                errMessage === 'internet連線已斷開。',
                errMessage === '無法完成作業。軟體導致連線失敗',
                errMessage === 'networkerror when attempting to fetch resource.',
                errMessage === '無法連接伺服器。'
            ].some(e => e))
    );
}
export function isFailedToLoadError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'load failed';
}
export function isFailedToFetchError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return errName === errObjTypes.TYPE_ERROR && errMessage === 'failed to fetch';
}
export function isPreFlightError(errObj = {}) {
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR && errMessage === 'preflight response is not successful'
    );
}
export function isCORSDueToServerNoResponse(errObj = {}) {
    // 當 server 無法回應時，包括無法回出正確 header 時，因為 header 沒有 allow origin 瀏覽器會當作 cors
    const errName = toLowerCase(errObj.name);
    const errMessage = toLowerCase(errObj.message);
    return (
        errName === errObjTypes.TYPE_ERROR &&
        errMessage.includes('is not allowed by access-control-allow-origin')
    );
}
// Due to long term expired
export function isUnauthorizedToGetAccessToken(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 401 && message === 'unauthorized.';
}
export function isFeverApiAccessTokenExpired(errObj = {}) {
    const { code } = normalizeApiErrInfo(errObj);
    return code === 'maj-r1-fu1';
}
export function isFormApiDuplicateSubmitFormError(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 422 && message === '2-1003-0001-21';
}
export function isFormApiAccessTokenExpired(errObj = {}) {
    const { message, code } = normalizeApiErrInfo(errObj);
    return message === '1-3003-1004-22' && code === '401';
}
export function isGetWinnerEmptySuspendError(errObj) {
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'pm-v0-f0';
}
export function isGetWinnerDuplicateError(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'etf-v0-f0' && message === 'entry flow - already exists';
}
export function isGetWinnerEmptyContinueError(errObj) {
    const { status, code } = normalizeApiErrInfo(errObj);
    return status === 226 && code === 'lt-v0-f0';
}
export function isGetWinnerWhenKKboxOrderUnfulfillErr(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'etf-v0-f0' && message === 'kkpoint order - entry flow';
}
export function isFormApiSetGameResultWhenSuspendErr(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 422 && message === '3-0001-0301-31';
}
export function isRequestAtInvalidPromoTime(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 403 && message === 'it is not time now';
}
export function isErrorWhenPromoSuspend(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'pm-v0-f0' && message === 'promotion status is pause';
}
export function isErrorWhenPromoEnd(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'cudf-v0-f0' && message === 'promotion is over';
}
export function isErrOfGetEntryWhenPromoInvalid(errObj = {}) {
    // 非上線狀態、或活動時間未開放
    const { code, message } = normalizeApiErrInfo(errObj);
    return code === 'cudf-au0-f0' && message === 'promotion is invalid.';
}

export function isErrOfGetEntryWhenReCaptchaMissing(errObj = {}) {
    // 啟用 reCaptcha 但沒有帶入 reCaptcha token
    const { code, message } = normalizeApiErrInfo(errObj);
    return code === 'cudf-au0-f0' && message === 'recaptcha token authorize failed - empty';
}

export function isErrOfCyberbizEcLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "MemberCyberbiz - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('membercyberbiz')
    );
}

// 新會員註冊
export function isErrOfNewRegisterMemberLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "RegistrationDate - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('registrationdate')
    );
}
export function isErrOfMemberTier(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "MemberTier - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' && failed_type === 'qualification' && message.includes('membertier')
    );
}
export function isErrOfApiNameListLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "RestrictedList - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message.includes('restrictedlist')
    );
}
export function isErrOfMemberOrderLimit(errObj = {}) {
    const {
        code,
        data: { failed_type },
        message
    } = normalizeApiErrInfo(errObj);
    // 完整 message 為 "OrderList - member accessibility not allowed"
    return (
        code === 'cudf-v0-f0' && failed_type === 'qualification' && message.includes('orderlist')
    );
}
export function isErrOfEnterpriseBindLine(errObj = {}) {
    const {
        status,
        code,
        data: { failed_type, redirectUrl },
        message
    } = normalizeApiErrInfo(errObj);

    const isEnterpriseBindLineError =
        status === 401 &&
        code === 'cudf-v0-f0' &&
        failed_type === 'qualification' &&
        message === 'enterprisebonded - member accessibility not allowed';
    const isLineMemberBindWithinDaysError =
        status === 400 && code === 'malbb-3rd-f0' && message === 'qualification link days failed';
    const isLineMemberNotLinkedError =
        status === 400 &&
        code === 'malbb-3rd-f0' &&
        message === 'member not have link account' &&
        Boolean(redirectUrl);

    return (
        isEnterpriseBindLineError || isLineMemberBindWithinDaysError || isLineMemberNotLinkedError
    );
}
export function isEntriesOverdueError(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 403 && message === 'entry overdue';
}
export function isGetEntriesLimitError(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'entry total limit - validate failed';
}

// 方案人次已滿 - https://app.clickup.com/t/86eptk8a0
export function isCreateEntryTotalMonthlyEntryLimitError(errObj) {
    const { message, code } = normalizeApiErrInfo(errObj);
    return code === 'ent-v0-f0' && message === 'total monthly entry limit - validate failed';
}
export function isGetEntriesPlayTimeLimitErr(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    // ent-v0-f0 是 pkg 整合產生出的 code
    return (
        status === 403 &&
        (code === 'et-v0-f0' || code === 'ent-v0-f0') &&
        ['totalplaytimes', 'dailyplaytimes', 'period time data exists'].includes(message)
    );
}
export function isSetGameResultWhenContestModuleRecalculatingRank(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'vt-c0-f0' && message.startsWith('please wait a moment');
}
export function isUgcVoteDuplicate(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return status === 500 && code === 'vt-c0-f0' && message === 'vote failed';
}
export function isVoteOnNoSignupFormUgc(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'media library not already';
}
export function isVoteOnNotQualifiedUgc(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'media library validate failed';
}
export function isVoteLimitError(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    // ent-v0-f0 是 pkg 整合產生出的 code
    return (
        status === 403 &&
        (code === 'et-v0-f0' || code === 'ent-v0-f0') &&
        message === 'vote times validate failed'
    );
}
export function isLineLoginCredentialErr(errObj) {
    const { status, code, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 && code === 'cudf-3rd-f0' && message === 'line friendship status api error'
    );
}
export function isTooManyAttemptErr(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 429 && message === 'too many attempts.';
}
export function isErrPromoSubmitRecaptchaExpired(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    return (
        status === 400 && code === 'cudf-au0-f0' && message === 'recaptcha token authorize failed'
    );
}
export function isErrCreateEntriesTimeOut(errObj) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 500 && message.includes('operation timed out');
}
export function isErrMemberPointsNotEnough(errObj) {
    const { status, message, code } = normalizeApiErrInfo(errObj);
    return status === 400 && code === 'psm-v0-f0' && message === 'member points is not enough';
}

export function isErrOfTagLimit(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return (
        status === 400 &&
        (message === 'user have exclude tag' || message === 'user not have group tag')
    );
}

export function isPromoRelatedAndCanNotUpdate(errObj = {}) {
    const { status, message } = normalizeApiErrInfo(errObj);
    return status === 400 && message === 'this promotion be related to other promtoion';
}

export function normalizeApiErrInfo(errObj = {}) {
    const { method, httpStatus, payload: { code, data, message } = {} } =
        errObj.extraInfo ?? errObj;
    return {
        method,
        status: httpStatus,
        data: data ?? {},
        code: String(code).toLowerCase(),
        message: String(message).toLowerCase()
    };
}

export const feverErrCodes = {
    FAILED_TO_FETCH: 'fv-g-ff-001',
    FAILED_TO_LOAD: 'fv-g-fl-001',
    ERR_PREFLIGHT: 'fv-g-pf-001',
    FAILED_RESP_CORS: 'fv-g-sfc-001',
    ERR_DNS_LOOK_UP: 'fv-g-dl-001',
    ERR_SSL_CONNECT: 'fv-g-ssl-001',
    ERR_WEBKIT: 'fv-g-wk-001',
    ERR_DUPLICATE_GET_WINNER: 'fv-p-gw-001',
    ERR_FORM_DUPLICATE: 'fv-f-001',
    ERR_CREATE_ENTRIES_TIMEOUT: 'fv-ce-001'
};

export function getGeneralErrCode(errObj) {
    switch (true) {
        case isFailedToFetchError(errObj):
            const failedToFetchError = generateApiErrObj(errObj, clientErrTypes.FAILED_TO_FETCH);

            return {
                code: feverErrCodes.FAILED_TO_FETCH,
                error: failedToFetchError
            };
        case isFailedToLoadError(errObj):
            const failedToLoadError = generateApiErrObj(errObj, clientErrTypes.FAILED_TO_LOAD);

            return {
                code: feverErrCodes.FAILED_TO_LOAD,
                error: failedToLoadError
            };
        case isPreFlightError(errObj):
            const preflightResponseIsNotSuccessfulError = generateApiErrObj(
                errObj,
                clientErrTypes.PREFLIGHT_RESPONSE_IS_NOT_SUCCESSFUL
            );

            return {
                code: feverErrCodes.ERR_PREFLIGHT,
                error: preflightResponseIsNotSuccessfulError
            };
        case isCORSDueToServerNoResponse(errObj):
            const notAllowedByAccessControlAllowOriginError = generateApiErrObj(
                errObj,
                clientErrTypes.NOT_ALLOWED_BY_ACCESS_CONTROL_ALLOW_ORIGIN
            );

            return {
                code: feverErrCodes.FAILED_RESP_CORS,
                error: notAllowedByAccessControlAllowOriginError
            };
        case isDNSLookupError(errObj):
            const dnsLookUpFailedError = generateApiErrObj(
                errObj,
                clientErrTypes.DNS_LOOK_UP_FAILED
            );

            return {
                code: feverErrCodes.ERR_DNS_LOOK_UP,
                error: dnsLookUpFailedError
            };
        case isSSLConnectionError(errObj):
            const sslConnectionError = generateApiErrObj(
                errObj,
                clientErrTypes.SSL_CONNECTION_ERROR
            );

            return {
                code: feverErrCodes.ERR_SSL_CONNECT,
                error: sslConnectionError
            };
        case isWebKitError(errObj):
            const webKitError = generateApiErrObj(errObj, clientErrTypes.WEB_KIT_ERROR);

            return {
                code: feverErrCodes.ERR_WEBKIT,
                error: webKitError
            };
        default:
            return {};
    }
}

export const highTrafficErrCodes = new Set([
    feverErrCodes.FAILED_TO_FETCH,
    feverErrCodes.FAILED_TO_LOAD,
    feverErrCodes.ERR_PREFLIGHT,
    feverErrCodes.FAILED_RESP_CORS
]);
